import React from 'react';
import { useLocation } from 'react-router-dom';
import { routeConfig } from '../../config/routes.config';
import { hasAnyPermission, checkModuleAccess, checkFeatureAccess } from '../../services/menu/permissionMapping';
import { usePrivileges } from '../../helpers/license/usePermissions';
import NoAccessMessageResultNotAuthorized from './PageExceptions/NoAccessMessageResultNotAuthorized';
import { Result } from 'antd';

/**
 * ProtectedRoute Component
 *
 * Validates route access by:
 * 1. Checking if route exists in routeConfig
 * 2. Checking required license features (e.g. items_advanced)
 * 3. Checking user permissions
 * 4. Checking module access
 *
 * Shows error page if route doesn't exist or user doesn't have access
 */
const ProtectedRoute = ({ children, path }) => {
  const location = useLocation();
  const { permissions, module_access, license_features } = usePrivileges();
  const currentPath = path || location.pathname;

  // Find route in configuration
  // Try exact match first, then pattern match, then starts-with match
  const route = routeConfig.find(r => {
    // Exact match
    if (r.exact && r.path === currentPath) {
      return true;
    }
    
    // Pattern match (for dynamic routes)
    if (r.matchPattern) {
      if (r.matchPattern instanceof RegExp) {
        return r.matchPattern.test(currentPath);
      }
      if (typeof r.matchPattern === 'string') {
        const pattern = new RegExp(r.matchPattern);
        return pattern.test(currentPath);
      }
    }
    
    // Starts-with match (for non-exact routes)
    if (!r.exact && currentPath.startsWith(r.path)) {
      return true;
    }
    
    return false;
  });

  // If route doesn't exist, show 404
  if (!route) {
    return (
      <Result
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
      />
    );
  }

  // Check license features (e.g. items_advanced for configurable templates/groups)
  const routeFeatures = route.features || [];
  if (routeFeatures.length > 0) {
    const hasFeatureAccess = checkFeatureAccess(routeFeatures, license_features || []);
    if (!hasFeatureAccess) {
      return <NoAccessMessageResultNotAuthorized />;
    }
  }

  // Check permissions - user must have at least one required permission
  const routePermissions = route.permissions || [];
  if (routePermissions.length > 0) {
    const hasAccess = hasAnyPermission(routePermissions, permissions);
    if (!hasAccess) {
      return <NoAccessMessageResultNotAuthorized />;
    }
  }

  // Check module access - user must have access to at least one required module
  const routeModules = route.modules || [];
  if (routeModules.length > 0) {
    // module_access is an array of module keys that are enabled
    const hasModuleAccess = checkModuleAccess(routeModules, module_access);
    if (!hasModuleAccess) {
      return <NoAccessMessageResultNotAuthorized />;
    }
  }

  // All checks passed, render the component
  return <>{children}</>;
};

export default ProtectedRoute;

